import { lastPathSegment } from "@/lib/utils";
import { Handle, type Node, type NodeProps, Position } from "@xyflow/react";
import React, { useContext } from "react";
import { idSort } from "../../pages/internal-debugger/InternalDebuggerPage";
import { NodeGraphSolveContext } from "./InternalDebuggerNodeGraph";
import { SelectedNodeContext } from "./InternalDebuggerNodeInfoPanel";
import InternalDebuggerStateValueView from "./InternalDebuggerStateValueView";
import "./InternalDebuggerRuleNode.scss";
import type { GraphNode } from "@imminently/decisively-core";

type SolveGraph = NonNullable<NonNullable<React.ContextType<typeof NodeGraphSolveContext>>["graph"]>;
const EMPTY_STATE: any[] = [];
const stateByRuleNode = new WeakMap<object, Map<string, any[]>>();

/** The solve graph's shown nodes grouped by the rule node they're of (last path segment), sorted: once per graph. */
const solveStateIndex = (graph: SolveGraph) => {
  let index = stateByRuleNode.get(graph);
  if (!index) {
    index = new Map();
    for (const id of graph.ids()) {
      if (graph.field(id, "hidden")) continue;
      const v = graph.deref(id);
      const value = graph.node(id) as any;
      const key = lastPathSegment(v);
      let list = index.get(key);
      if (!list) index.set(key, (list = []));
      list.push({ v, value });
    }
    for (const list of index.values()) list.sort(idSort);
    stateByRuleNode.set(graph, index);
  }
  return index;
};

const InternalDebuggerRuleNode = (props: NodeProps<Node<GraphNode>>) => {
  const { data } = props;
  const solve = useContext(NodeGraphSolveContext);
  const { selected, setSelected } = useContext(SelectedNodeContext);

  const nodeState: any[] | undefined = solve?.graph ? (solveStateIndex(solve.graph).get(data.id) ?? EMPTY_STATE) : undefined;

  const handleClick = () => {
    setSelected(data.id);
  };

  return (
    <div
      tabIndex={0}
      onClick={handleClick}
      className={`RuleNode ${selected === data.id ? "--selected" : ""}`}
    >
      {data.id === solve?.goal && <b>Goal</b>}
      {data.inferred && <b>Inferred</b>}
      <div>{data.description}</div>
      <small>{data.id?.slice(0, 8)}</small>
      <br />
      <Handle
        type="target"
        position={Position.Top}
        style={{ borderRadius: 0 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="a"
        style={{ borderRadius: 0 }}
      />
      <div className="RuleNode__state">
        {nodeState ? nodeState.map((node) => <InternalDebuggerStateValueView state={node} />) : null}
      </div>
    </div>
  );
};

export default InternalDebuggerRuleNode;
